import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Drawer,
  Stack,
  Typography,
  IconButton,
  Chip,
  Divider,
  Box,
  CircularProgress,
  Tooltip,
  Button,
  Switch,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { ChipBadge } from 'new/elements/ChipBadge';
import {
  Close as CloseIcon,
  Code as CodeIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { ReactComponent as ToolCropIcon } from 'new/components/icons/fi-rr-tool-crop.svg';
import { ReactComponent as PlugIcon } from 'new/components/icons/fi-rr-plug.svg';
import { ReactComponent as ToolIcon } from 'new/components/icons/tool.svg';
import { Tabs } from 'new/elements/Tabs';
import { TextField } from 'new/elements/TextField';
import { CopyableText } from '../../components/CopyableText';
import { formatRelativeTime, formatDateTime } from '../../utils/dateFormatters';
import { getStatusLabel, getToolKindLabel, getToolDisplayName } from '../../utils/toolLabels';
import { extractApiError } from '../../utils/errorUtils';
import { useSnackbar } from 'new/contexts/SnackbarContext';
import { ROUTE_SETTINGS_AUTHENTICATION } from 'new/constants/pathnames';
import {
  useGetToolsetQuery,
  useGetToolsetMCPConfigQuery,
  useGetToolsetToolsQuery,
  useGetToolsetChangelogsQuery,
  useUpdateToolsetMutation,
  useSyncToolsetMutation,
} from '../../state/aiApi';
import type {
  ApiToolset,
  ApiToolsetTool,
  ApiToolsetChangelog,
  ApiMCPConfigVariant,
} from '../../state/aiApiTypes';
import { getStatusChipColor } from 'new/modules/ai/utils/statusColorHelpers';
import { ExtMcpServersPanel, GatewayToolsCatalog } from './GatewaySection';
import styles from './ToolsetDetailsDrawer.module.scss';

interface ToolsetDetailsDrawerProps {
  toolsetId: number | null;
  open: boolean;
  onClose: () => void;
  initialEditMode?: boolean;
}

function buildTabs(gatewayEnabled: boolean) {
  const tabs = [
    { value: 'details', label: 'Details' },
    { value: 'mcp-config', label: 'MCP Config' },
  ];

  if (gatewayEnabled) {
    tabs.push({ value: 'gateway', label: 'Gateway' });
  }
  tabs.push({ value: 'activity', label: 'Activity' });
  return tabs;
}

function getChangeTypeLabel(changeType: string): string {
  switch (changeType) {
    case 'created':
      return 'Created';
    case 'metadata_updated':
      return 'Metadata Updated';
    case 'tools_added':
      return 'Tools Added';
    case 'tools_removed':
      return 'Tools Removed';
    case 'tools_changed':
      return 'Tools Changed';
    case 'manifest_updated':
      return 'Manifest Updated';
    case 'status_changed':
      return 'Status Changed';
    case 'deleted':
      return 'Deleted';
    default:
      return changeType?.replace(/_/g, ' ') || 'Unknown';
  }
}

function getChangeTypeColor(
  changeType: string,
): 'success' | 'warning' | 'error' | 'info' | 'default' {
  switch (changeType) {
    case 'created':
      return 'success';
    case 'tools_added':
      return 'success';
    case 'tools_removed':
      return 'warning';
    case 'status_changed':
      return 'info';
    case 'deleted':
      return 'error';
    default:
      return 'default';
  }
}

function getTriggerTypeLabel(triggerType: string): string {
  switch (triggerType) {
    case 'user_action':
      return 'User';
    case 'nexset_sync':
      return 'Auto-sync';
    case 'system':
      return 'System';
    case 'api':
      return 'API';
    default:
      return triggerType || 'Unknown';
  }
}

function getVariantLabel(variantKey: string): string {
  switch (variantKey) {
    case 'service_key':
      return 'Service Key';
    case 'oauth_google':
      return 'Google OAuth';
    case 'oauth_azure':
      return 'Azure OAuth';
    default:
      return variantKey;
  }
}

function getVariantDescription(variantKey: string): string {
  switch (variantKey) {
    case 'service_key':
      return 'Authenticate with a Nexla service key via Bearer token header.';
    case 'oauth_google':
      return 'Authenticate with Google SSO. Users sign in with their Google account.';
    case 'oauth_azure':
      return 'Authenticate with Azure Entra SSO. Users sign in with their Microsoft account.';
    default:
      return '';
  }
}

// Tool item
const ToolItem: React.FC<{ tool: ApiToolsetTool }> = ({ tool }) => (
  <div className={styles.toolItem}>
    <Stack direction="row" alignItems="flex-start" spacing={1.5}>
      <div className={styles.toolItemIcon}>
        <ToolIcon />
      </div>
      <div className={styles.toolItemContent}>
        <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
          <Typography variant="body2" fontWeight={500} className={styles.toolName}>
            {getToolDisplayName(tool)}
          </Typography>
          {tool.tool_kind && (
            <ChipBadge label={getToolKindLabel(tool.tool_kind)} variant="outlined" />
          )}
        </Stack>
        {tool.tool_description && (
          <Typography variant="caption" color="text.secondary" className={styles.toolDescription}>
            {tool.tool_description}
          </Typography>
        )}
        <Stack direction="row" spacing={2} mt={0.5} minWidth={0}>
          {tool.tool_key && (
            <Typography variant="caption" color="text.secondary" className={styles.toolKey}>
              <CodeIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />

              {tool.tool_key}
            </Typography>
          )}
        </Stack>
      </div>
    </Stack>
  </div>
);

// Changelog entry
const ChangelogEntry: React.FC<{ entry: ApiToolsetChangelog }> = ({ entry }) => (
  <div className={styles.changelogEntry}>
    <Stack direction="row" alignItems="center" spacing={1}>
      <Chip
        label={getChangeTypeLabel(entry.change_type)}
        size="small"
        color={getChangeTypeColor(entry.change_type)}
        variant="outlined"
        sx={{ height: 22, fontSize: 12 }}
      />

      <Chip
        label={getTriggerTypeLabel(entry.trigger_type)}
        size="small"
        variant="outlined"
        sx={{ height: 22, fontSize: 12 }}
      />

      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
        {formatRelativeTime(entry.created_at)}
      </Typography>
    </Stack>
    {entry.field_changes && Object.keys(entry.field_changes).length > 0 && (
      <Stack spacing={0.5} sx={{ mt: 1 }}>
        {Object.entries(entry.field_changes).map(([field, change]) => (
          <Typography
            key={field}
            variant="caption"
            color="text.secondary"
            sx={{ wordBreak: 'break-all' }}
          >
            <strong>{field}</strong>: {String(change.old ?? '(none)')} →{' '}
            {String(change.new ?? '(none)')}
          </Typography>
        ))}
      </Stack>
    )}
    {entry.tool_changes && (
      <Stack spacing={0.25} sx={{ mt: 1 }}>
        {entry.tool_changes.added?.map((t) => (
          <Typography key={`add-${t.tool_id}`} variant="caption" color="success.main">
            + {t.tool_key || `Tool ${t.tool_id}`}
          </Typography>
        ))}
        {entry.tool_changes.removed?.map((t) => (
          <Typography key={`rm-${t.tool_id}`} variant="caption" color="error.main">
            − {t.tool_key || `Tool ${t.tool_id}`}
          </Typography>
        ))}
        {entry.tool_changes.modified?.map((t) => (
          <Typography key={`mod-${t.tool_id}`} variant="caption" color="text.primary">
            ~ {t.tool_key || `Tool ${t.tool_id}`}
            {t.summary ? `: ${t.summary}` : ''}
          </Typography>
        ))}
      </Stack>
    )}
  </div>
);

// MCP Config Variant Card with copy functionality
const MCPVariantCard: React.FC<{
  variantKey: string;
  variant: ApiMCPConfigVariant;
  serverConfig?: { command: string; args: string[] };
}> = ({ variantKey, variant, serverConfig }) => {
  const [copied, setCopied] = useState(false);

  const config = serverConfig || variant.server;
  const jsonString = useMemo(() => {
    if (!config) return '';
    const obj = {
      mcpServers: {
        [variant.server_name]: config,
      },
    };
    return JSON.stringify(obj, null, 2);
  }, [config, variant.server_name]);

  const handleCopy = useCallback(() => {
    if (!jsonString) return;
    navigator.clipboard.writeText(jsonString).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [jsonString]);

  return (
    <div className={styles.variantCard}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2">{getVariantLabel(variantKey)}</Typography>
        </Stack>
        <Tooltip title={copied ? 'Copied!' : 'Copy config'}>
          <IconButton
            size="small"
            onClick={handleCopy}
            className={`${styles.variantCopyButton} ${copied ? styles.copied : ''}`}
          >
            {copied ? (
              <CheckIcon fontSize="small" color="success" />
            ) : (
              <CopyIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {getVariantDescription(variantKey)}
      </Typography>
      {variant.mcp_url && (
        <CopyableText
          label="MCP URL"
          value={variant.mcp_url}
          fieldId={`variant-${variantKey}-url`}
          maxWidth="100%"
        />
      )}
      {jsonString && (
        <div className={styles.jsonViewer} style={{ marginTop: 'var(--spacing-md)' }}>
          <pre className={styles.jsonContent}>{jsonString}</pre>
        </div>
      )}
    </div>
  );
};

// MCP Config viewer — shows all variants
const MCPConfigViewer: React.FC<{
  toolset: ApiToolset;
  toolsetId: number;
}> = ({ toolset, toolsetId }) => {
  const [copiedAll, setCopiedAll] = useState(false);
  const { data: mcpConfig, isLoading } = useGetToolsetMCPConfigQuery(toolsetId);

  const variants = mcpConfig?._meta?.variants;
  const mcpServers = mcpConfig?.mcpServers;

  const fullJsonString = useMemo(() => {
    if (!mcpConfig) return '';
    return JSON.stringify({ mcpServers: mcpConfig.mcpServers }, null, 2);
  }, [mcpConfig]);

  const handleCopyAll = useCallback(() => {
    if (!fullJsonString) return;
    navigator.clipboard.writeText(fullJsonString).then(() => {
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    });
  }, [fullJsonString]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!mcpConfig) {
    return (
      <div className={styles.section}>
        <Typography variant="body2" color="text.secondary" className={styles.emptyState}>
          MCP configuration is not available for this toolset.
          {toolset.status !== 'active' && ' Activate the toolset to generate MCP configuration.'}
        </Typography>
      </div>
    );
  }

  // If we have variants, show them individually
  if (variants && Object.keys(variants).length > 0) {
    return (
      <div className={styles.section}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="subtitle2" sx={{ mb: 0 }}>
            MCP Client Configuration
          </Typography>
          <Tooltip title={copiedAll ? 'Copied!' : 'Copy all configs'}>
            <IconButton size="small" onClick={handleCopyAll}>
              {copiedAll ? (
                <CheckIcon fontSize="small" color="success" />
              ) : (
                <CopyIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Add any of these configurations to your MCP client (Cursor, Claude Desktop, VS Code,
          etc.).
        </Typography>
        <Stack spacing={2}>
          {Object.entries(variants).map(([key, variant]) => (
            <MCPVariantCard
              key={key}
              variantKey={key}
              variant={variant}
              serverConfig={mcpServers?.[variant.server_name]}
            />
          ))}
        </Stack>
      </div>
    );
  }

  // Fallback: show raw JSON like before
  return (
    <div className={styles.section}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="subtitle2" sx={{ mb: 0 }}>
          MCP Client Configuration
        </Typography>
        <Tooltip title={copiedAll ? 'Copied!' : 'Copy JSON'}>
          <IconButton size="small" onClick={handleCopyAll}>
            {copiedAll ? (
              <CheckIcon fontSize="small" color="success" />
            ) : (
              <CopyIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Add this configuration to your MCP client (e.g. Cursor, Claude Desktop, VS Code, etc.).
        Replace {'<YOUR_SERVICE_KEY>'} with your Nexla service key.
      </Typography>
      <div className={styles.jsonViewer}>
        <pre className={styles.jsonContent}>{fullJsonString}</pre>
      </div>
    </div>
  );
};

// Activity tab
const ActivityTab: React.FC<{ toolsetId: number }> = ({ toolsetId }) => {
  const { data: changelogs = [], isLoading } = useGetToolsetChangelogsQuery({
    id: toolsetId,
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (changelogs.length === 0) {
    return (
      <div className={styles.section}>
        <Typography variant="body2" color="text.secondary" className={styles.emptyState}>
          No activity recorded for this toolset yet.
        </Typography>
      </div>
    );
  }

  return (
    <div className={styles.section}>
      <Typography variant="subtitle2" className={styles.sectionTitle}>
        Recent Activity
      </Typography>
      <Stack spacing={1.5}>
        {changelogs.map((entry) => (
          <ChangelogEntry key={entry.id} entry={entry} />
        ))}
      </Stack>
    </div>
  );
};

export const ToolsetDetailsDrawer: React.FC<ToolsetDetailsDrawerProps> = ({
  toolsetId,
  open,
  onClose,
  initialEditMode = false,
}) => {
  const [activeTab, setActiveTab] = useState<string>('details');
  const [isEditing, setIsEditing] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [isDescriptionOverflowing, setIsDescriptionOverflowing] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);
  const [editGatewayEnabled, setEditGatewayEnabled] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const initialEditModeApplied = useRef(false);
  const { showSuccess, showError } = useSnackbar();

  // RTK Query — fetch toolset details and tools
  const { data: toolset, isLoading: isLoadingToolset } = useGetToolsetQuery(toolsetId!, {
    skip: !toolsetId || !open,
  });
  const { data: tools = [] } = useGetToolsetToolsQuery(toolsetId!, {
    skip: !toolsetId || !open,
  });
  const { data: mcpConfig } = useGetToolsetMCPConfigQuery(toolsetId!, {
    skip: !toolsetId || !open,
  });
  const [updateToolset, { isLoading: isUpdating }] = useUpdateToolsetMutation();
  const [syncToolset, { isLoading: isSyncing }] = useSyncToolsetMutation();

  const handleSync = async () => {
    if (!toolsetId) return;
    try {
      const result = await syncToolset(toolsetId).unwrap();
      if (result.updated_versions > 0) {
        showSuccess(`Toolset synced — ${result.updated_versions} tool(s) updated`);
      } else if (result.errors === 0) {
        showSuccess('Toolset synced — all tools up to date');
      }
      if (result.errors > 0) {
        showError(`${result.errors} tool(s) failed to sync`);
      }
    } catch (err) {
      showError(extractApiError(err, 'Failed to sync toolset'));
    }
  };

  // Reset scroll position when collapsing so text always shows from the top.
  useEffect(() => {
    if (!descriptionExpanded && descriptionRef.current) {
      descriptionRef.current.scrollTop = 0;
    }
  }, [descriptionExpanded]);

  // Detect overflow by measuring the clipped Box (not the Typography).
  // When collapsed the Box has maxHeight + overflow:hidden, so
  // scrollHeight > clientHeight reliably signals hidden content.
  useEffect(() => {
    if (descriptionExpanded) return;
    const id = requestAnimationFrame(() => {
      const el = descriptionRef.current;
      if (el) {
        setIsDescriptionOverflowing(el.scrollHeight > el.clientHeight);
      } else {
        setIsDescriptionOverflowing(false);
      }
    });
    return () => cancelAnimationFrame(id);
  }, [toolset?.description, open, descriptionExpanded]);

  // Reset tab and edit mode when drawer closes
  useEffect(() => {
    if (!open) {
      setActiveTab('details');
      setIsEditing(false);
      setDescriptionExpanded(false);
      setIsDescriptionOverflowing(false);
      initialEditModeApplied.current = false;
    }
  }, [open]);

  // Enter edit mode if initialEditMode is set — only once per open
  useEffect(() => {
    if (open && initialEditMode && toolset && !initialEditModeApplied.current) {
      initialEditModeApplied.current = true;
      setIsEditing(true);
      setEditName(toolset.name || '');
      setEditDescription(toolset.description || '');
      setEditTags(toolset.tags || []);
      setEditGatewayEnabled(toolset.mcp_gateway_enabled ?? false);
    }
  }, [open, initialEditMode, toolset]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  const handleStartEdit = () => {
    if (!toolset) return;
    setEditName(toolset.name || '');
    setEditDescription(toolset.description || '');
    setEditTags(toolset.tags || []);
    setEditGatewayEnabled(toolset.mcp_gateway_enabled ?? false);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setTagInput('');
  };

  const handleSaveEdit = async () => {
    if (!toolsetId || !editName.trim()) return;
    try {
      await updateToolset({
        id: toolsetId,
        data: {
          name: editName.trim(),
          description: editDescription.trim(),
          tags: editTags,
          mcp_gateway_enabled: editGatewayEnabled,
        },
      }).unwrap();
      showSuccess('Toolset updated successfully');
      setIsEditing(false);
    } catch (err) {
      showError(extractApiError(err, 'Failed to update toolset'));
    }
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !editTags.includes(tag)) {
      setEditTags([...editTags, tag]);
    }
    setTagInput('');
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setEditTags(editTags.filter((t) => t !== tagToRemove));
  };

  if (!toolsetId) return null;

  const isLoading = isLoadingToolset;
  const hasTools = tools.length > 0;
  const hasTechnicalDetails = toolset?.manifest_hash || toolset?.tools_hash;
  const gatewayEnabled = toolset?.mcp_gateway_enabled ?? false;
  const tabs = buildTabs(gatewayEnabled);
  const hasTags = toolset?.tags && toolset.tags.length > 0;
  const mcpVariants = mcpConfig?._meta?.variants;
  const googleOAuthUrl = mcpVariants?.oauth_google?.mcp_url;
  const azureOAuthUrl = mcpVariants?.oauth_azure?.mcp_url;
  const hasMcpAccess = toolset?.mcp_url || googleOAuthUrl || azureOAuthUrl;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { width: 480, maxWidth: '100%' },
        }}
      >
        <div className={styles.drawerContent}>
          {/* Header */}
          <div className={styles.header}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
              <Stack direction="row" alignItems="flex-start" spacing={1.5} flex={1}>
                <div className={styles.packageIcon}>
                  <PlugIcon />
                </div>
                <div className={styles.headerContent}>
                  <Typography variant="h6" className={styles.headerTitle}>
                    {toolset?.name || 'Loading...'}
                  </Typography>
                </div>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {toolset && (
                  <Tooltip title="Sync All Tools">
                    <span>
                      <IconButton onClick={handleSync} size="small" disabled={isSyncing}>
                        {isSyncing ? <CircularProgress size={16} /> : <SyncIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {toolset && (
                  <Tooltip title="Edit">
                    <IconButton onClick={handleStartEdit} size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <IconButton onClick={onClose} size="small">
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Stack>

            {toolset?.description && (
              <Box className={styles.descriptionContainer}>
                <Box
                  ref={descriptionRef}
                  className={
                    descriptionExpanded ? styles.descriptionScrollBox : styles.descriptionClipBox
                  }
                >
                  <Typography variant="body2" color="text.secondary">
                    {toolset.description}
                  </Typography>
                </Box>
                {isDescriptionOverflowing && (
                  <Typography
                    variant="caption"
                    color="primary"
                    component="button"
                    aria-expanded={descriptionExpanded}
                    className={styles.descriptionToggle}
                    onClick={() => setDescriptionExpanded((prev) => !prev)}
                  >
                    {descriptionExpanded ? 'Show less' : 'Show more'}
                  </Typography>
                )}
              </Box>
            )}

            {toolset?.status && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                className={styles.statusRow}
              >
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <ChipBadge
                    label={getStatusLabel(toolset.status)}
                    color={getStatusChipColor(toolset.status)}
                  />

                  {gatewayEnabled && (
                    <ChipBadge label="Gateway" color="secondary" variant="outlined" />
                  )}
                </Stack>
                {/*{(toolset.mcp_server_key || toolset.mcp_url) && (
                 <Typography variant="caption" color="text.secondary">
                   MCP server
                 </Typography>
                )}*/}
              </Stack>
            )}
          </div>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
            <Tabs tabs={tabs} value={activeTab} onChange={handleTabChange} />
          </Box>

          {isLoading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!isLoading && toolset && activeTab === 'details' && (
            <>
              {/* Quick Stats */}
              <div className={styles.statsSection}>
                <div className={styles.statItem}>
                  <Typography variant="caption" color="text.secondary">
                    TOOLS
                  </Typography>
                  <Typography variant="h6">{toolset.tool_count ?? tools.length}</Typography>
                </div>
                <div className={styles.statItem}>
                  <Typography variant="caption" color="text.secondary">
                    CREATED
                  </Typography>
                  <Typography variant="body2">{formatRelativeTime(toolset.created_at)}</Typography>
                </div>
                <div className={styles.statItem}>
                  <Typography variant="caption" color="text.secondary">
                    UPDATED
                  </Typography>
                  <Typography variant="body2">{formatRelativeTime(toolset.updated_at)}</Typography>
                </div>
              </div>

              <Divider />

              {/* Tags */}
              {hasTags && (
                <>
                  <div className={styles.section}>
                    <Typography variant="subtitle2" className={styles.sectionTitle}>
                      Tags
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {toolset.tags!.map((tag) => (
                        <Chip key={tag} label={tag} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </div>
                  <Divider />
                </>
              )}

              {/* MCP Access */}
              {hasMcpAccess && (
                <>
                  <div className={styles.section}>
                    <Typography variant="subtitle2" className={styles.sectionTitle}>
                      MCP Access
                    </Typography>
                    <Stack spacing={2}>
                      {toolset.mcp_url && (
                        <CopyableText
                          label="MCP URL"
                          value={toolset.mcp_url}
                          fieldId="toolset-mcp-url"
                          maxWidth="100%"
                        />
                      )}
                      {googleOAuthUrl && (
                        <CopyableText
                          label="MCP URL (Google OAuth)"
                          value={googleOAuthUrl}
                          fieldId="toolset-mcp-url-google-oauth"
                          maxWidth="100%"
                        />
                      )}
                      {azureOAuthUrl && (
                        <CopyableText
                          label="MCP URL (Azure OAuth)"
                          value={azureOAuthUrl}
                          fieldId="toolset-mcp-url-azure-oauth"
                          maxWidth="100%"
                        />
                      )}
                      {toolset.mcp_url && (
                        <MuiLink
                          component={Link}
                          to={ROUTE_SETTINGS_AUTHENTICATION}
                          target="_blank"
                          variant="body2"
                          className={styles.manageKeysLink}
                        >
                          Manage Service Keys
                          <OpenInNewIcon className={styles.openInNewIcon} />
                        </MuiLink>
                      )}
                    </Stack>
                  </div>
                  <Divider />
                </>
              )}

              {/* Tools */}
              <div className={styles.section}>
                <Typography variant="subtitle2" className={styles.sectionTitle}>
                  Tools ({tools.length})
                </Typography>
                {hasTools ? (
                  <Stack spacing={1}>
                    {tools.map((tool) => (
                      <ToolItem key={tool.tool_id} tool={tool} />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary" className={styles.emptyState}>
                    No tools in this toolset
                  </Typography>
                )}
              </div>

              {/* Technical Details */}
              {hasTechnicalDetails && (
                <>
                  <Divider />
                  <div className={styles.section}>
                    <Typography variant="subtitle2" className={styles.sectionTitle}>
                      Technical Details
                    </Typography>
                    <Stack spacing={1.5}>
                      <div className={styles.techItem}>
                        <Typography variant="caption" color="text.secondary">
                          Toolset ID
                        </Typography>
                        <Typography variant="body2" className={styles.techValue}>
                          {toolset.tool_set_id}
                        </Typography>
                      </div>
                      {toolset.manifest_hash && (
                        <div className={styles.techItem}>
                          <Typography variant="caption" color="text.secondary">
                            Manifest Hash
                          </Typography>
                          <Typography variant="body2" className={styles.techValue}>
                            {toolset.manifest_hash}
                          </Typography>
                        </div>
                      )}
                      {toolset.tools_hash && (
                        <div className={styles.techItem}>
                          <Typography variant="caption" color="text.secondary">
                            Tools Hash
                          </Typography>
                          <Typography variant="body2" className={styles.techValue}>
                            {toolset.tools_hash}
                          </Typography>
                        </div>
                      )}
                      {toolset.created_at && (
                        <div className={styles.techItem}>
                          <Typography variant="caption" color="text.secondary">
                            Created At
                          </Typography>
                          <Typography variant="body2">
                            {formatDateTime(toolset.created_at)}
                          </Typography>
                        </div>
                      )}
                      {toolset.updated_at && (
                        <div className={styles.techItem}>
                          <Typography variant="caption" color="text.secondary">
                            Updated At
                          </Typography>
                          <Typography variant="body2">
                            {formatDateTime(toolset.updated_at)}
                          </Typography>
                        </div>
                      )}
                    </Stack>
                  </div>
                </>
              )}
            </>
          )}

          {/* MCP Config Tab */}
          {!isLoading && toolset && activeTab === 'mcp-config' && (
            <MCPConfigViewer toolset={toolset} toolsetId={toolsetId} />
          )}

          {/* Gateway Tab */}
          {!isLoading && toolset && activeTab === 'gateway' && (
            <div className={styles.section}>
              <Stack spacing={3}>
                <ExtMcpServersPanel toolsetId={toolsetId} />
                <Divider />
                <GatewayToolsCatalog toolsetId={toolsetId} />
              </Stack>
            </div>
          )}

          {/* Activity Tab */}
          {!isLoading && toolset && activeTab === 'activity' && (
            <ActivityTab toolsetId={toolsetId} />
          )}
        </div>
      </Drawer>

      {/* Edit Toolset Dialog */}

      <Dialog open={isEditing} onClose={handleCancelEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Toolset</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Toolset name"
              fullWidth
              autoFocus
            />

            <TextField
              label="Description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Description (optional)"
              multiline
              rows={3}
              fullWidth
            />

            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">
                Tags
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {editTags.map((tag) => (
                  <Chip key={tag} label={tag} size="small" onDelete={() => handleRemoveTag(tag)} />
                ))}
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  size="small"
                  placeholder="Add tag..."
                  fullWidth
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                />

                <Button size="small" onClick={handleAddTag} disabled={!tagInput.trim()}>
                  Add
                </Button>
              </Stack>
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ pt: 1 }}
            >
              <div>
                <Typography variant="subtitle2">MCP Gateway</Typography>
                <Typography variant="caption" color="text.secondary">
                  Connect external MCP servers and access a unified tool catalog
                </Typography>
              </div>
              <Switch
                checked={editGatewayEnabled}
                onChange={(e) => setEditGatewayEnabled(e.target.checked)}
                size="small"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={isUpdating || !editName.trim()}
            startIcon={isUpdating ? <CircularProgress size={14} /> : undefined}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
